import React, { useEffect, useRef } from "react";
import { IconClose } from "./Icons";
import "./Drawer.css";

const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "textarea:not([disabled])",
  "select:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

export default function Drawer({ open, title, onClose, width = 460, children }) {
  const drawerRef = useRef(null);
  const previouslyFocusedRef = useRef(null);

  // Esc to close.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Focus trap + restore: when the drawer opens, remember the element that
  // had focus, move focus into the drawer, and keep Tab cycling inside.
  // On close, restore focus to the element that opened the drawer.
  useEffect(() => {
    if (!open) return undefined;
    previouslyFocusedRef.current = document.activeElement;
    const root = drawerRef.current;
    if (root) {
      const first = root.querySelector(FOCUSABLE);
      if (first && typeof first.focus === "function") first.focus();
    }
    const onKey = (e) => {
      if (e.key !== "Tab" || !root) return;
      const items = Array.from(root.querySelectorAll(FOCUSABLE)).filter(
        (el) => !el.hasAttribute("disabled") && el.offsetParent !== null
      );
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      const prev = previouslyFocusedRef.current;
      if (prev && typeof prev.focus === "function") {
        try { prev.focus(); } catch (_) {}
      }
      previouslyFocusedRef.current = null;
    };
  }, [open]);

  if (!open) return null;
  return (
    <div className="ca-drawer-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <aside
        ref={drawerRef}
        className="ca-drawer"
        style={{ width }}
        role="dialog"
        aria-modal="true"
        aria-label={title || "Panel"}
      >
        <header className="ca-drawer-header">
          <span className="ca-drawer-title">{title}</span>
          <button className="ca-drawer-close" onClick={onClose} title="Close" aria-label="Close">
            <IconClose size={16} />
          </button>
        </header>
        <div className="ca-drawer-body">{children}</div>
      </aside>
    </div>
  );
}
